/* eslint-disable react/prop-types */
import { ObjectCell } from '../types'
import clsx from 'clsx'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../shadcn'
import { createRenderer } from './renderer'
import { NOL } from './null'
import { Maximize, Minimize } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import Editor, { type OnMount } from '@monaco-editor/react'
import { EditorFooter } from './utils'

const isValidJson = (text: string) => {
  if (!text.trim()) {
    return true
  }
  try {
    JSON.parse(text)
    return true
  } catch (e: any) {
    return false
  }
}

export const ObjectRenderer = createRenderer<ObjectCell>({
  View: ({ className, cell }) => {
    if (cell.value === null) {
      return <NOL />
    }
    return (
      <div className={clsx(className, { 'computed-cell': cell.computed })}>
        <pre className="text-xs truncate font-mono tracking-tight">{JSON.stringify(cell.value)}</pre>
      </div>
    )
  },
  Editor: ({ className, cell, onCellChange, column, ...props }) => {
    const [editorValue, setEditorValue] = useState(JSON.stringify(cell.value, null, 2))
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false)

    const onValueChange = useCallback((value: string) => {
      setEditorValue(value)
    }, [])

    // Configure Monaco editor with JSON schema
    const handleEditorDidMount: OnMount = useCallback(
      (_, monaco) => {
        if (column.schema && Object.keys(column.schema).length > 0) {
          // Configure JSON schema for autocomplete
          monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
            validate: true,
            schemas: [
              {
                uri: '$schema',
                fileMatch: ['*'],
                schema: column.schema,
              },
            ],
          })
        }
      },
      [column.schema]
    )

    const handleSave = () => {
      if (!isValidJson(editorValue)) {
        toast.error('Cannot save: Invalid JSON.')
        return
      }
      try {
        onCellChange?.(
          props.row,
          {
            type: 'object',
            value: JSON.parse(editorValue) as object,
          },
          true
        )
        setIsFullscreen(false)
      } catch (e) {
        toast.error('An unexpected error occurred on save.')
      }
    }

    useEffect(() => {
      const stopEnterFromMonaco = (ev: KeyboardEvent) => {
        const el = ev.target as HTMLElement | null
        if (!el) return
        if (el.closest('.monaco-editor')) {
          if (ev.key === 'Enter' && !(ev as any).isComposing) {
            ev.stopPropagation()
          }
        }
        if (ev.key === 'Enter' && ev.metaKey) {
          handleSave()
        }
      }
      document.addEventListener('keydown', stopEnterFromMonaco, true) // capture!
      document.addEventListener('keyup', stopEnterFromMonaco, true)
      return () => {
        document.removeEventListener('keydown', stopEnterFromMonaco, true)
        document.removeEventListener('keyup', stopEnterFromMonaco, true)
      }
    }, [handleSave])

    return (
      <div className={clsx(className, 'h-full', { 'computed-cell': cell.computed })}>
        <Popover open={!isFullscreen} onOpenChange={handleSave}>
          <PopoverTrigger asChild>
            <div
              className={clsx(className, 'flex items-center w-full h-full pl-2', { 'computed-cell': cell.computed })}
            >
              {cell.value ? (
                <pre className="text-xs truncate font-mono tracking-tight">{JSON.stringify(cell.value)}</pre>
              ) : (
                <NOL />
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-96 shadow-2xl " align="start">
            <div className="w-full p-0 relative">
              <div className="absolute flex items-center gap-1 z-10 top-1 right-1">
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setIsFullscreen(true)}
                  title="Enter fullscreen"
                >
                  <Maximize className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Editor
                language="json"
                theme="vs-dark"
                height="180px"
                value={editorValue}
                onChange={(code) => code && onValueChange(code)}
                onMount={handleEditorDidMount}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  lineNumbers: 'off',
                  glyphMargin: false,
                  suggestOnTriggerCharacters: true,
                  quickSuggestions: true,
                  formatOnPaste: true,
                  acceptSuggestionOnEnter: 'smart',
                  autoIndent: 'advanced',
                  tabSize: 2,
                  insertSpaces: true,
                  wordBasedSuggestions: 'currentDocument',
                }}
              />
            </div>
            <EditorFooter handleSave={handleSave} />
          </PopoverContent>
        </Popover>

        <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
          <DialogContent hideCloseButton className="p-0 gap-0 w-[90vw] max-w-[90vw] h-[90vh] flex flex-col">
            <DialogHeader className="flex-row items-center justify-end p-2 border-b">
              <div className="flex items-center gap-1">
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    title="Exit fullscreen"
                  >
                    <Minimize className="w-4 h-4" />
                  </Button>
                </DialogClose>
              </div>
            </DialogHeader>
            <div className="flex-grow">
              <Editor
                language="json"
                theme="vs-dark"
                value={editorValue}
                onChange={(code) => code && onValueChange(code)}
                onMount={handleEditorDidMount}
                options={{
                  minimap: { enabled: true },
                  scrollBeyondLastLine: true,
                  glyphMargin: true,
                  suggestOnTriggerCharacters: true,
                  quickSuggestions: true,
                  formatOnPaste: true,
                  acceptSuggestionOnEnter: 'smart',
                  autoIndent: 'advanced',
                  tabSize: 2,
                  insertSpaces: true,
                  wordBasedSuggestions: 'currentDocument',
                }}
              />
            </div>
            <DialogFooter className="p-0">
              <EditorFooter handleSave={handleSave} />
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  },
})
